export interface StatusPermissions {
  /** Typing in the input and pressing send. */
  canSendMessage: boolean;
  /** Voice notes and attachments. */
  canSendMedia: boolean;
  /**
   * Sending a WhatsApp-call permission request. Only offered on 'active'
   * channels; an already granted approval (permanent or temporary) is never
   * gated by channel status.
   */
  canRequestWhatsAppCall: boolean;
  /**
   * Transferring the chat to another guide. Only offered on 'active'
   * channels — the backend's transfer flow notifies the client with a
   * free-form WhatsApp message, which is only deliverable inside the
   * 24-hour window.
   */
  canTransferChat: boolean;
  /**
   * Ending the chat. Only offered on 'active' channels — an inactive
   * chat is already ended, and an 'in conversation' chat belongs to
   * another guide.
   */
  canEndChat: boolean;
  /**
   * Why the composer is blocked; set whenever canSendMessage is false.
   * A blocked input is only marked read-only (web) / non-editable (native),
   * and neither carries any styling of its own — without this the box looks
   * live, takes focus, and silently swallows every keystroke, which reads as
   * a frozen app. Say why it is blocked and how (or whether) it will lift.
   */
  blockedInputPlaceholder?: string;
}

// Input permissions derived from the channel's custom_data.status.
//
// 'pending' is not a status the backend stores: it is derived in
// StreamMessage::buildChannelFromConversation as `status === active &&
// !is_sendable`, i.e. the guide has already spent their one free-form reply
// and the client has not answered yet. is_sendable flips back to true when the
// client sends a text (Api\Wa\MessageController), which returns the channel to
// 'active'. The 24-hour window is a separate mechanism — when it lapses,
// SetConversationsToInactiveCommand moves the conversation to 'inactive', not
// 'pending'.
//
// The template picker stays mounted in every status, but on 'pending' a
// template send is rejected by WhatsApp too — so nothing reaches the client
// until they answer.
//
// Free-form sending (text, attachments, voice notes) is allowed only on
// 'active': on 'inactive' the 24-hour window has lapsed so WhatsApp rejects
// anything but a template, and an 'in conversation' chat belongs to another
// guide. A channel with no status at all falls through to the default and
// stays sendable — only known non-active statuses block.
//
// 'guide finder active' (WaConversations::GUIDE_FINDER_ACTIVE) is an active
// conversation that the backend leaves untranslated so the app can label it;
// it grants exactly the same permissions as 'active'.
const ACTIVE_STATUSES = ['active', 'guide finder active'];

export const getStatusPermissions = (status?: string): StatusPermissions => {
  const normalized = status?.toLowerCase();
  const isActive = normalized !== undefined && ACTIVE_STATUSES.includes(normalized);
  switch (normalized) {
    case 'pending':
      return {
        canSendMessage: false, canSendMedia: false, canRequestWhatsAppCall: false, canTransferChat: false, canEndChat: false,
        // Deliberately suggests no action: on 'pending' WhatsApp rejects
        // template sends too, so there is genuinely nothing to do but wait.
        blockedInputPlaceholder: "Waiting for the client's reply — you can send again once they answer",
      };
    case 'inactive':
      return {
        canSendMessage: false, canSendMedia: false, canRequestWhatsAppCall: false, canTransferChat: false, canEndChat: false,
        blockedInputPlaceholder: 'This chat is inactive — send a template to start a new conversation',
      };
    case 'in conversation':
      return {
        canSendMessage: false, canSendMedia: false, canRequestWhatsAppCall: false, canTransferChat: false, canEndChat: false,
        blockedInputPlaceholder: 'Another guide is handling this chat',
      };
    default:
      return { canSendMessage: true, canSendMedia: true, canRequestWhatsAppCall: isActive, canTransferChat: isActive, canEndChat: isActive };
  }
};
